import { useOrganizationPartners } from '@/api'
import { idmQueryKeys } from '@/api/momo/idm/idm-query-keys'
import { updateCurrentPartner } from '@/api/momo/idm/users'
import { useAuthContext } from '@/hooks/use-auth-context'
import { Button, Menu } from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { Spinner } from '../../spinner'
import { toastError } from '../../toast/toast'
import { Icon } from '@/components/icon'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'

export function PartnerDropdown() {
  const queryClient = useQueryClient()
  const { account } = useAuthContext()
  const pathname = usePathname()
  const router = useRouter()

  const showDropdown =
    pathname.startsWith('/agreements') ||
    pathname.startsWith('/branches') ||
    pathname.startsWith('/claim-management') ||
    pathname.startsWith('/pricesheets')

  const { mutate: setCurrentPartnerID, isPending } = useMutation({
    mutationFn: async (partnerID: string) => {
      return updateCurrentPartner(partnerID)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: idmQueryKeys.users.current()
      })
    },
    onError: () => {
      toastError(
        'An unexpected error occurred while updating your selected partner.'
      )
    }
  })

  const { data: partners, isFetching: isFetchingPartners } =
    useOrganizationPartners(account?.OrganizationID)

  useEffect(() => {
    if (!partners || partners?.length === 0) {
      return
    }

    if (isPending) {
      return
    }

    // If the user doesn't have a current partner set, default to the first partner in the list.
    if (!account?.CurrentPartnerID) {
      setCurrentPartnerID(partners[0].ID)
    }

    // If for some reason the current partner is no longer partnered with the user's organization, reset the current partner to the first partner in the list.
    if (
      account?.CurrentPartnerID &&
      !partners?.some((partner) => partner.ID === account?.CurrentPartnerID)
    ) {
      setCurrentPartnerID(partners[0].ID)
    }
  }, [partners, account?.CurrentPartnerID, setCurrentPartnerID, isPending])

  // Get the currently selected partner.
  const currentPartner = useMemo(() => {
    return partners?.find((partner) => partner.ID === account?.CurrentPartnerID)
  }, [partners, account?.CurrentPartnerID])

  // For the dropdown, filter out the current partner so it doesn't display twice.
  const filteredPartners = useMemo(() => {
    return (
      partners?.filter((partner) => partner.ID !== account?.CurrentPartnerID) ??
      []
    )
  }, [partners, account?.CurrentPartnerID])

  // Find the partner by ID and set it as the current partner.
  const setPartner = (organizationID: string) => {
    const partner = partners?.find((p) => p.ID === organizationID)
    if (partner) {
      setCurrentPartnerID(partner.ID)

      // If the user is on a page with a list of items (price sheets, claims, transactions), don't navigate away.
      if (
        pathname === '/agreements' ||
        pathname === '/branches' ||
        pathname === '/branches/mappings' ||
        pathname === '/claim-management/claims' ||
        pathname === '/claim-management/transactions' ||
        pathname === '/pricesheets'
      ) {
        return
      }

      // If the user is on an agreement page, navigate back to the list of agreements.
      if (pathname.startsWith('/agreements/')) {
        router.push('/agreements')
        return
      }

      // If the user is on a page for a specific claim, navigate back to the list of claims.
      if (pathname.startsWith('/claim-management/claims')) {
        router.push('/claim-management/claims')
        return
      }

      // If the user is on a page for a specific transaction, navigate back to the list of transactions.
      if (pathname.startsWith('/claim-management/transactions')) {
        router.push('/claim-management/transactions')
        return
      }

      // If the user is on a page for a specific price sheet, navigate back to the list of price sheets.
      if (pathname.startsWith('/pricesheets')) {
        router.push('/pricesheets')
        return
      }

      router.push('/')
    }
  }

  // For now, only show the dropdown on pages that use it.
  if (!showDropdown) {
    return null
  }

  // If the user has no partners, don't display anything.
  if (partners?.length === 0) {
    return null
  }

  // If partners are loading, display a spinner.
  if (isFetchingPartners && !partners) {
    return <Spinner color="light" />
  }

  // If there's no current partner set, show the spinner (toast will show on error).
  if (!account?.CurrentPartnerID) {
    return null
  }

  // If the user has only one partner, display the partner name as text.
  if (filteredPartners.length === 0) {
    return <span className="font-semibold">{currentPartner?.Name}</span>
  }

  // If the user has multiple partners, display a dropdown.
  return (
    <Menu
      shadow="md"
      position="bottom-start"
      styles={{ dropdown: { minWidth: 170, maxWidth: 360 } }}
    >
      <Menu.Target>
        <Button
          variant="transparent"
          c="white"
          rightSection={<Icon icon={faChevronDown} size={10} />}
        >
          {currentPartner?.Name}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {filteredPartners.map((partner) => {
          return (
            <Menu.Item
              key={partner.ID}
              className="font-medium text-[var(--mantine-color-celerityBrand-9)] hover:text-[var(--mantine-color-celerityBrand-5)]"
              onClick={() => setPartner(partner.ID)}
            >
              {partner.Name}
            </Menu.Item>
          )
        })}
      </Menu.Dropdown>
    </Menu>
  )
}
